import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Seo } from "@/components/Seo";

const FOUNDER_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Person",
  name: "Almeer kilee Nance",
  jobTitle: "CEO & Author",
  url: "https://1123publishing.com/authors",
  worksFor: { "@type": "Organization", name: "1123 Publishing Company", url: "https://1123publishing.com" },
  description:
    "Almeer kilee Nance is the CEO of 1123 Publishing Company and author of Feral: Arise and Remember Who You Are, Poe Dad no Dad, and The Crown Path Initiative. A leading voice for justice-impacted authors and youth mentorship programs.",
};

export default function Authors() {
  const [authors, setAuthors] = useState([]);
  useEffect(() => { api.get("/authors").then((r) => setAuthors(r.data)); }, []);

  return (
    <div data-testid="authors-page">
      <Seo
        title="The Authors Collective | Almeer kilee Nance | 1123 Publishing"
        description="Meet the 1123 Authors Collective, led by CEO and author Almeer kilee Nance. Every book on our list passes through a real editorial desk — and every author joins an enduring community."
        path="/authors"
        type="profile"
        jsonLd={FOUNDER_SCHEMA}
      />
      <section className="bg-parchment py-20 md:py-28 border-b border-navy/10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="overline mb-3">The 1123 Authors Collective</div>
          <h1 className="font-serif text-5xl md:text-6xl text-navy">A house, not a self-published shelf.</h1>
          <p className="mt-6 text-charcoal/80 leading-relaxed max-w-2xl mx-auto">
            1123 Publishing is led by CEO and author Almeer kilee Nance and is opening its imprint to debut and established voices alike.
            Every book on our list passes through a real editorial desk &mdash; and every author becomes part of an enduring community.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-10 space-y-20">
          {authors.map((a, i) => (
            <div key={a.id} className={`grid md:grid-cols-12 gap-10 items-center ${i % 2 ? "md:[&>*:first-child]:order-2" : ""}`}>
              <div className="md:col-span-4">
                <div className="aspect-[3/4] bg-navy border border-gold/40 relative overflow-hidden">
                  <img src={a.portrait} alt="Almeer kilee Nance, CEO of 1123 Publishing" className="w-full h-full object-cover opacity-70 mix-blend-luminosity" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/20 to-transparent" />
                  <div className="absolute bottom-5 left-5 right-5 text-ivory">
                    <div className="overline text-gold">CEO & Author</div>
                    <div className="font-serif text-2xl mt-1">{a.name}</div>
                  </div>
                </div>
              </div>
              <div className="md:col-span-8">
                <div className="overline mb-3">Inaugural Voice</div>
                <h2 className="font-serif text-3xl md:text-4xl text-navy">{a.name}</h2>
                <p className="mt-4 text-charcoal/85 leading-relaxed">{a.bio}</p>
                {a.quote && (
                  <blockquote className="mt-6 border-l-2 border-gold pl-5 font-serif text-xl italic text-navy/80">
                    &ldquo;{a.quote}&rdquo;
                  </blockquote>
                )}
                <div className="mt-6 overline">On the List</div>
                <ul className="mt-2 text-navy/85 space-y-1">
                  {a.books.map((b) => <li key={b} className="font-serif text-lg">&#10086; {b}</li>)}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Forthcoming voices — actively reading submissions */}
      <section className="py-16 md:py-20 bg-parchment border-t border-navy/10" data-testid="forthcoming-voices">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="text-center mb-10">
            <div className="overline">Forthcoming Voices</div>
            <h2 className="font-serif text-3xl md:text-4xl text-navy mt-3">The next names on the list.</h2>
            <p className="mt-4 max-w-xl mx-auto text-charcoal/75 text-sm leading-relaxed">
              Our editorial desk is actively reading submissions for the 2026&ndash;2027 lists.
              The names below will appear here as soon as their contracts are signed.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[1, 2, 3].map((n) => (
              <div key={n} className="aspect-[4/5] border border-navy/20 bg-white relative flex flex-col items-center justify-center text-center p-8">
                <div className="overline text-gold">Author</div>
                <div className="mt-3 font-serif text-3xl text-navy/35">To be announced</div>
                <div className="ornament-rule mt-6 w-24"><span className="text-sm">&#10086;</span></div>
                <div className="mt-6 text-[10px] tracking-[0.25em] uppercase text-navy/40">Reading Period · {2026}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-navy text-ivory text-center">
        <div className="max-w-2xl mx-auto px-6">
          <div className="overline text-gold mb-3">Submissions Open</div>
          <h2 className="font-serif text-4xl md:text-5xl">Become a 1123 author.</h2>
          <p className="mt-4 text-ivory/80">Send us your manuscript. Our editorial desk reads everything.</p>
          <a href="/submit" data-testid="authors-cta-submit"
            className="inline-block mt-7 bg-gold text-navy px-8 py-4 text-[11px] uppercase tracking-[0.24em] font-semibold hover:bg-white transition-colors">
            Submit Your Manuscript
          </a>
        </div>
      </section>
    </div>
  );
}
